import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function AdminPage() {
  const [mods, setMods] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMod, setEditingMod] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    category: 'MLO',
    tags: '',
    author: '',
    deliveryType: 'code',
    downloadLink: '',
  });

  useEffect(() => {
    fetchMods();
  }, []);

  const fetchMods = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mods`);
      setMods(response.data);
    } catch (error) {
      console.error('Error fetching mods:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const modData = {
      name: formData.name,
      description: formData.description,
      price: parseInt(formData.price) * 100, // Convert to cents
      image: formData.image,
      category: formData.category,
      tags: formData.tags.split(',').map((tag) => tag.trim()),
      author: formData.author,
      deliveryType: formData.deliveryType,
      downloadLink: formData.deliveryType === 'gdrive' ? formData.downloadLink : null,
    };

    try {
      if (editingMod) {
        await axios.put(`${API_URL}/api/admin/mods/${editingMod.id}`, modData);
        alert('Mod updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/admin/mods`, modData);
        alert('Mod added successfully!');
      }

      setFormData({
        name: '',
        description: '',
        price: '',
        image: '',
        category: 'MLO',
        tags: '',
        author: '',
        deliveryType: 'code',
        downloadLink: '',
      });
      setEditingMod(null);
      setShowAddForm(false);
      fetchMods();
    } catch (error) {
      console.error('Error saving mod:', error);
      alert('Failed to save mod');
    }
  };

  const handleEdit = (mod) => {
    setEditingMod(mod);
    setFormData({
      name: mod.name,
      description: mod.description,
      price: (mod.price / 100).toString(),
      image: mod.image,
      category: mod.category,
      tags: mod.tags.join(', '),
      author: mod.author,
      deliveryType: mod.deliveryType,
      downloadLink: mod.downloadLink || '',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mod?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/mods/${id}`);
      alert('Mod deleted successfully!');
      fetchMods();
    } catch (error) {
      console.error('Error deleting mod:', error);
      alert('Failed to delete mod');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      image: '',
      category: 'MLO',
      tags: '',
      author: '',
      deliveryType: 'code',
      downloadLink: '',
    });
    setEditingMod(null);
    setShowAddForm(false);
  };

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-6 py-3 bg-primary hover:bg-blue-600 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined">
            {showAddForm ? 'close' : 'add'}
          </span>
          {showAddForm ? 'Cancel' : 'Add New Mod'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-surface-dark border border-white/5 rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">
            {editingMod ? 'Edit Mod' : 'Add New Mod'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mod Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Price (USD) *
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="29.99"
                  className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  <option value="MLO">MLO</option>
                  <option value="Vehicle">Vehicle</option>
                  <option value="Script">Script</option>
                  <option value="Clothing">Clothing</option>
                  <option value="UI / HUD">UI / HUD</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Delivery Type *
                </label>
                <select
                  name="deliveryType"
                  value={formData.deliveryType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                >
                  <option value="code">Download Code (Random XXX-XXX-XXX)</option>
                  <option value="gdrive">Google Drive Link</option>
                </select>
              </div>

              {formData.deliveryType === 'gdrive' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Google Drive Link *
                  </label>
                  <input
                    type="url"
                    name="downloadLink"
                    value={formData.downloadLink}
                    onChange={handleInputChange}
                    required={formData.deliveryType === 'gdrive'}
                    placeholder="https://drive.google.com/file/d/..."
                    className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="3"
                className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Image URL *
              </label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags (comma-separated) *
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                required
                placeholder="ESX, QBCore, Optimized"
                className="w-full px-4 py-2 bg-background-dark border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="px-6 py-3 bg-primary hover:bg-blue-600 text-white font-bold rounded-lg transition-colors"
              >
                {editingMod ? 'Update Mod' : 'Add Mod'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors border border-white/10"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mods List */}
      <div className="bg-surface-dark border border-white/5 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-white/5">
          <h2 className="text-2xl font-bold text-white">All Mods ({mods.length})</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-dark">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Delivery
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {mods.map((mod) => (
                <tr key={mod.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <img
                        src={mod.image}
                        alt={mod.name}
                        className="w-12 h-12 rounded object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-white">{mod.name}</div>
                        <div className="text-xs text-gray-400">By {mod.author}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-white/5 text-gray-300 rounded">
                      {mod.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-mono">
                    ${(mod.price / 100).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {mod.deliveryType === 'code' ? (
                      <span className="px-2 py-1 text-xs font-medium bg-green-500/10 text-green-400 rounded border border-green-500/20">
                        Code
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 rounded border border-blue-500/20">
                        G-Drive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(mod)}
                        className="px-3 py-1 bg-primary/20 hover:bg-primary text-primary hover:text-white rounded transition-colors text-xs font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(mod.id)}
                        className="px-3 py-1 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded transition-colors text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
